'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Divider, IcDelete, IconButton, LoadingDark, Padding, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { updateSnsLinksService } from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';
import { z } from 'zod';
import { useCuratorBroadcastPublisher } from '../../common';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { PLATFORMS } from '../../join/funnels/SNSFunnel/constants/platforms';
import { curatorQueries } from '../../query-factory';
import { SNSPlatformSelector } from './SNSPlatformSelector';
import { SnsUrlInput } from './SnsUrlInput';
import { SubmitButton } from './SubmitButton';

const MAX_SNS_LINKS = 2;
const PRIMARY_PLATFORM_INDEX = 0; // 플랫폼 순서가 변경되지 않는다고 가정하고, 0번째 인덱스가 주요 플랫폼임

const urlInputVariants = tv({
  variants: {
    hasError: {
      true: 'border-red-500',
      false: '',
    },
  },
  defaultVariants: {
    hasError: false,
  },
});

const snsLinkSchema = z.object({
  platform: z.string().min(1, '플랫폼을 선택해주세요'),
  url: z.string().min(1, 'URL을 입력해주세요'),
});

const snsLinkEditFormSchema = z.object({
  snsLinks: z
    .array(snsLinkSchema)
    .min(1, '최소 1개의 SNS 링크를 추가해주세요')
    .max(MAX_SNS_LINKS, `최대 ${MAX_SNS_LINKS}개까지 SNS 링크를 추가해주세요`),
});

const getFullPrefix = (prefix?: string) => {
  if (prefix === '' || prefix === undefined) {
    return '';
  }
  return `https://www.${prefix}`;
};

export type SNSLink = z.infer<typeof snsLinkSchema>;
export type SNSLinkEditFormData = z.infer<typeof snsLinkEditFormSchema>;

interface Props {
  className?: string;
}

export function SNSLinkEditForm({ className }: Props) {
  const { navigateBack } = useNavigate();
  const router = useRouter();
  const searchParams = useSearchParams();
  // 신청 validation Dialog 등 외부 진입에서 등록 후 자동 복귀할 URL.
  // 동일 origin 내부 경로만 허용 (open redirect 방지: '/' 시작 + '//' 시작 차단).
  const returnToRaw = searchParams?.get('returnTo') ?? null;
  const returnTo =
    returnToRaw?.startsWith('/') && !returnToRaw.startsWith('//') ? returnToRaw : null;
  const snackbar = useCuratorSnackbar();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const { isWebview, service } = useDeviceInfo();

  const userInfoQuery = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfoQuery.data?.snsTitle ?? '';
  const profileQuery = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(snsTitle),
    enabled: Boolean(snsTitle),
  });

  const isPending = userInfoQuery.isPending || profileQuery.isPending;

  const methods = useForm<SNSLinkEditFormData>({
    resolver: zodResolver(snsLinkEditFormSchema),
    values: {
      snsLinks: (profileQuery.data?.snsLinks || []).map(({ platform, url }) => {
        const platformInfo = PLATFORMS.find((p) => p.id === platform);
        const idWithoutDomain =
          platformInfo?.id === 'ETC'
            ? url
            : url.replace(`${getFullPrefix(platformInfo?.prefix)}`, '');
        return {
          platform,
          url: idWithoutDomain,
        };
      }),
    },
  });

  const { control, handleSubmit, setValue } = methods;

  const { mutate: updateSnsLinks, isPending: isUpdateSnsLinksPending } = useMutation({
    mutationFn: (data: SNSLinkEditFormData) => updateSnsLinksService({ snsLinks: data.snsLinks }),
    onSuccess: () => {
      if (service === SERVICE.MUSINSA && snsTitle) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'profile_edit_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: snsTitle,
          name: 'link',
          section_name: 'curator_profile',
        });
      }
      snackbar.success('SNS 링크가 수정되었습니다.');
      if (returnTo) {
        router.replace(returnTo);
      } else {
        navigateBack();
      }
      if (snsTitle) {
        triggerQuerySync({
          queryKeys: [curatorQueries.profile.profileBySnsTitle(snsTitle).queryKey],
        });
      }
    },
    onError: (error: Error) => {
      snackbar.error(error.message);
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'snsLinks',
  });

  const onFormSubmit = (data: SNSLinkEditFormData) => {
    const processedLinks = data.snsLinks.map((link) => {
      const platformInfo = PLATFORMS.find(
        (platform) => platform.id.toUpperCase() === link.platform,
      );

      if (!platformInfo || platformInfo.id === 'ETC') {
        return link; // 플랫폼 정보가 없으면 원본 그대로 반환
      }

      return {
        ...link,
        url: `${getFullPrefix(platformInfo.prefix + link.url)}`,
      };
    });

    updateSnsLinks({ snsLinks: processedLinks });
  };

  // submit button 로직을 별도 컴포넌트로 분리하여 리렌더링 최적화

  const onAddSNSLink = () => {
    append({
      platform: '',
      url: '',
    });
  };

  if (isPending) {
    return isWebview ? null : (
      <div className="w-full flex items-center justify-center grow">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className={twMerge('w-full bg-white flex flex-col grow', className)}
      >
        <div className="grow pb-[72px]!">
          {fields.map((field, index) => (
            <div key={field.id}>
              <Padding className="flex flex-col">
                <Padding x="4" y="2" className="flex flex-col gap-[8px]">
                  <div className="flex items-center justify-between">
                    <Typography variant="body14pxMed">
                      {index === PRIMARY_PLATFORM_INDEX ? '플랫폼' : '추가 플랫폼'}
                    </Typography>
                    {/* TODO: 아이콘 변경할 것 */}
                    {index !== PRIMARY_PLATFORM_INDEX && (
                      <IconButton
                        aria-label="삭제"
                        className="shrink-0"
                        size="size16x16"
                        strokeColor="gray-500"
                        onClick={() => remove(index)}
                      >
                        <IcDelete strokeColor="black" />
                      </IconButton>
                    )}
                  </div>
                  <div>
                    <Controller
                      name={`snsLinks.${index}.platform`}
                      control={control}
                      render={({ field: platformField }) => (
                        <SNSPlatformSelector
                          value={platformField.value}
                          onChange={(value) => {
                            platformField.onChange(value);
                            setValue(`snsLinks.${index}.url`, '');
                          }}
                        />
                      )}
                    />
                  </div>
                </Padding>
                <Controller
                  name={`snsLinks.${index}.platform`}
                  control={control}
                  render={({ field: platformField }) => (
                    <Padding x="4" y="2" className="flex flex-col gap-[8px]">
                      <Typography variant="body14pxMed">
                        {PLATFORMS.find((p) => p.id === platformField.value)?.name} 주소
                      </Typography>
                      <Controller
                        name={`snsLinks.${index}.url`}
                        control={control}
                        render={({ field: urlField, fieldState }) => (
                          <SnsUrlInput
                            platform={PLATFORMS.find((p) => p.id === platformField.value)}
                            value={urlField.value}
                            onChange={urlField.onChange}
                            index={index}
                            className={urlInputVariants({
                              hasError: !!fieldState.error,
                            })}
                            debounceDelay={500}
                          />
                        )}
                      />
                    </Padding>
                  )}
                />
                <Padding x="4" y="4">
                  <Divider variant="line" />
                </Padding>
              </Padding>
            </div>
          ))}

          {fields.length < MAX_SNS_LINKS && (
            <button type="button" onClick={onAddSNSLink} className="w-full">
              <Padding x="4" y="2">
                <Typography variant="body13pxMed" className="underline">
                  다른 플랫폼 추가하기
                </Typography>
              </Padding>
            </button>
          )}
        </div>

        <SubmitButton isLoading={isUpdateSnsLinksPending} />
      </form>
    </FormProvider>
  );
}
